import type { Reg } from '../store/types';
import { brYmd, parseYmd, today } from '../lib/date';
import { dinheiro } from '../lib/moeda';
import { t } from '../lib/i18n';
import { C } from '../components/theme';

/** Dias de hoje até a data (negativo = já passou). */
export const diasAte = (data: string, hoje = today()) => Math.round((parseYmd(data).getTime() - parseYmd(hoje).getTime()) / 864e5);
export const diasEntre = (de: string, ate: string) => Math.round((parseYmd(ate).getTime() - parseYmd(de).getTime()) / 864e5);

/** "hoje", "amanhã", "em 5 dias", "há 3 dias" com a cor de alerta. */
export function contagem(data: string | null | undefined, feito = false): { txt: string; cor: string } | null {
  if (!data) return null;
  const n = diasAte(data);
  const txt = n === 0 ? t('hoje') : n === 1 ? t('amanhã') : n === -1 ? t('ontem') : n > 0 ? t('em {n} dias', { n }) : t('há {n} dias', { n: -n });
  const cor = feito ? C.mute : n < 0 ? C.danger : n <= 3 ? C.warn : n <= 15 ? C.text : C.sub;
  return { txt, cor };
}

export const num = (v: unknown) => (typeof v === 'number' && isFinite(v) ? v : typeof v === 'string' && v.trim() && isFinite(Number(v.replace(',', '.'))) ? Number(v.replace(',', '.')) : 0);
export const temNum = (v: unknown) => v !== '' && v != null && isFinite(num(v)) && (typeof v === 'number' || /\d/.test(String(v)));
export const din = (v: unknown, moeda: string) => dinheiro(num(v), moeda);
export const dataBr = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? brYmd(v) : '');
export const junta = (...p: (string | false | null | undefined | 0)[]) => p.filter(Boolean).join(' · ');
export const pct = (a: number, b: number) => (b > 0 ? Math.max(0, Math.min(100, Math.round((a / b) * 100))) : 0);
export const soma = (l: Reg[], k: string) => l.reduce((s, r) => s + num(r.c[k]), 0);
/** Texto da opção (as opções são guardadas em português e traduzidas ao mostrar). */
export const op = (v: unknown) => (v ? t(String(v)) : '');

// ─── Moeda de cada valor (0.69, Parte B1): não converte; com moedas diferentes, os totais saem separados ───
/** Moeda de um valor da ficha: cada valor pode ter a sua (c.moedas[k]); os outros valores da ficha seguem a do valor principal; sem escolha = a do app. */
export const moedaDo = (c: Record<string, any> | null | undefined, k: string, padrao: string): string => String(c?.moedas?.[k] || c?.moedas?.valor || padrao);
/** Valor de um campo de dinheiro na moeda dele (v: outro número na mesma moeda, ex.: o valor pago). */
export const dinDe = (r: { c: Record<string, any> }, k: string, padrao: string, v: unknown = r.c[k]) => din(v, moedaDo(r.c, k, padrao));
/** "R$", "US$", "€"... para mostrar na frente do campo de valor. */
export const simboloMoeda = (m: string) => dinheiro(0, m).replace(/[\d\s.,\u00a0\u202f-]/g, '') || m;
export type Somas = Map<string, number>;
/** Soma separada por moeda. */
export function somaPorMoeda(l: Reg[], valor: (r: Reg) => number, padrao: string, k = 'valor'): Somas {
  const m: Somas = new Map();
  for (const r of l) { const v = valor(r); if (!v) continue; const md = moedaDo(r.c, k, padrao); m.set(md, (m.get(md) ?? 0) + v); }
  return m;
}
export const temSoma = (m: Somas) => [...m.values()].some(v => Math.abs(v) >= 0.005);
/** a − b, moeda por moeda. */
export const menosSomas = (a: Somas, b: Somas): Somas => { const m: Somas = new Map(a); b.forEach((v, k) => m.set(k, (m.get(k) ?? 0) - v)); return m; };
/** "R$ 500,00 + US$ 100,00" (a moeda do app primeiro); sem valores = zero na moeda do app. */
export function dinSomas(m: Somas, padrao: string): string {
  const l = [...m.entries()].filter(([, v]) => Math.abs(v) >= 0.005).sort(([a], [b]) => (a === padrao ? -1 : b === padrao ? 1 : a.localeCompare(b)));
  return l.length ? l.map(([md, v]) => dinheiro(v, md)).join(' + ') : dinheiro(0, padrao);
}
